// src/components/operationCenter/widgets/StationarityWidget.tsx
// Stationarity dashboard: Hurst Exponent with threshold line and status badge.

import React, { useMemo } from "react";
import { FlaskConical } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps } from "@/types/dashboard";
import { useSignalSeries } from "@/hooks/signals/useSignalSeries";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import {
  createGrid,
  createXAxis,
  createTooltip,
  CHART_COLORS,
} from "@/utils/charts/theme";
import { formatDateLabel } from "@/utils/charts/dataUtils";

interface StationarityConfig {
  hurst_signal: string;
  adf_signal: string;
  run_id?: string;
}

function formatTooltipDate(raw: string): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} — ${hh}:${mm}`;
}

// ─── Component ─────────────────────────────────────────────────────────────

const StationarityWidget: React.FC<WidgetProps> = ({ widget }) => {
  const config = widget.config as StationarityConfig;
  const hurstSignal = "hurst";

  const hurstQuery = useSignalSeries({
    signalName: hurstSignal,
    ...(config.run_id != null ? { runId: config.run_id } : {}),
  });

  // ── Hurst data ──
  const hurst = useMemo(() => {
    if (!hurstQuery.data?.data || hurstQuery.data.data.length === 0) {
      return {
        dates: [] as string[],
        values: [] as (number | null)[],
        current: null,
      };
    }
    const pts = hurstQuery.data.data.slice(-100);
    return {
      dates: pts.map((p) => p.ts),
      values: pts.map((p) => p.signal_value),
      current: pts[pts.length - 1]?.signal_value ?? null,
    };
  }, [hurstQuery.data]);

  // ── Status badge ──
  const hurstCurrent = hurst.current;
  const hurstStatus = useMemo(() => {
    if (hurstCurrent == null) return null;
    return hurstCurrent < 0.5
      ? {
          text: "Mean-Reverting",
          color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
        }
      : {
          text: "Trending",
          color: "text-red-400 bg-red-500/10 border-red-500/30",
        };
  }, [hurstCurrent]);

  // ── Chart option ──
  const hurstOption = useMemo((): EChartsOption => {
    if (hurst.dates.length === 0) return {};

    return {
      backgroundColor: "transparent",
      tooltip: createTooltip({
        trigger: "axis",
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const p = params[0] as { axisValue: string; value: number | null };
          const val = p.value;
          if (val == null) return "";
          const ok = val < 0.5;
          const color = ok ? "#22c55e" : "#ef4444";
          return `<div style="font-weight:500;margin-bottom:2px;color:#9ca3af;font-size:10px;">${formatTooltipDate(p.axisValue)}</div>
            <div style="font-weight:600;color:${color};font-size:11px;">${val.toFixed(4)}</div>`;
        },
      }),
      grid: createGrid({
        top: 8,
        right: 36,
        bottom: 50,
        left: 8,
        containLabel: true,
      }),
      dataZoom: [
        { type: "inside" },
        {
          type: "slider",
          show: true,
          bottom: 8,
          height: 18,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af", fontSize: 9 },
          brushSelect: false,
          labelFormatter: (_value: number, valueStr: string) =>
            formatTooltipDate(valueStr),
        },
      ],
      xAxis: {
        ...createXAxis({ data: hurst.dates, boundaryGap: false }),
        axisLabel: {
          color: CHART_COLORS.axis.label,
          margin: 14,
          showMaxLabel: true,
          formatter: (val: string) => formatDateLabel(val, "short"),
        },
      },
      yAxis: {
        type: "value",
        scale: true,
        axisLabel: {
          color: CHART_COLORS.axis.label,
          fontSize: 9,
          formatter: (v: number) => v.toFixed(2),
        },
        splitLine: { show: false },
        axisLine: { show: false },
      },
      series: [
        {
          type: "line",
          data: hurst.values,
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 1.5, color: "#a78bfa" },
          itemStyle: { color: "#a78bfa" },
          areaStyle: { color: "rgba(167,139,250,0.09)" },
          markLine: {
            silent: true,
            symbol: ["none", "none"],
            animation: false,
            data: [
              {
                yAxis: 0.5,
                lineStyle: {
                  color: "#f59e0b",
                  width: 1,
                  type: "dashed" as const,
                },
                label: {
                  show: true,
                  formatter: "H=0.5",
                  position: "insideEndTop" as const,
                  fontSize: 9,
                  color: "#f59e0b",
                },
              },
            ],
          },
        },
      ],
    } as EChartsOption;
  }, [hurst.dates, hurst.values]);

  const hasHurst = hurst.dates.length > 0;

  if (hurstQuery.isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (!hasHurst) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <FlaskConical className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No stationarity data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wide text-slate-300">
            Hurst Exponent
          </span>
          <span
            className={`text-[10px] font-semibold tabular-nums ${hurst.current != null && hurst.current < 0.5 ? "text-emerald-400" : "text-red-400"}`}
          >
            {hurst.current != null ? hurst.current.toFixed(4) : "—"}
          </span>
        </div>
        {hurstStatus && (
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${hurstStatus.color}`}
          >
            {hurstStatus.text}
          </span>
        )}
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0">
        <EChartsRenderer
          chartOption={hurstOption}
          height="100%"
          hasData
          containerClassName=""
        />
      </div>
    </div>
  );
};

export default StationarityWidget;
